/** external */
import _ from "lodash";
import React, {
  useRef,
  useEffect,
  useState,
  memo,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import { elevations } from "@toddle-edu/ds-theme";
import { DropdownMenu, Tooltip } from "@toddle-edu/ds-web";
import { TagOutlined } from "@toddle-edu/ds-icons";

/** internal */
import {
  scrollIntoView,
  isElementInViewPortVertical,
  isTabletByUserAgent,
} from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { useDebouncedCallback } from "Utils/CustomHooks";
import MediaModal from "UIComponents/MediaModal";
import { reorderTools } from "ClassRoom/modules/utils";
import { useI18n } from "Hooks";
import {
  TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  MYP_OBJECTIVE_RUBRIC,
} from "Constants/stringConstants";
import QuestionInsightsField from "WorksheetInsights/components/QuestionInsightsField";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getStudentAssignmentFromCacheV2 } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { isMobileOrTablet, isSafeExamBrowser } from "Utils/DeviceDetectHelpers";

/** relative */
import {
  CONTENT_UID_TEXT_BOX,
  FIELD_UID_RICH_TEXT_EDITOR,
  FIELD_UID_RESOURCES,
  UPDATE_DEBOUNCE_CALL_TIME,
  DYNAMIC_FIELDS,
  WORKSHEET_MODES,
  MODE_TEACHER_CREATE,
  MODE_STUDENT_ATTEMPT,
  MODE_TEACHER_EVALUATE,
  MODE_TEACHER_VIEW,
  FIELD_UID_BLANKS,
  FIELD_UID_BLANKS_ANSWERS,
  COMMENT_FIELD_DETAILS,
  MODE_STUDENT_EVALUATE,
  FIELD_UID_MCQ_CHOICES,
  FIELD_UID_MCQ_RESPONSE,
  FIELD_UID_MCQ_ANSWERS,
  FIELD_UID_SCORE,
  MODE_TEACHER_PREVIEW,
  MODE_ASSESSMENT_VIEW,
  MODE_LIBRARY_VIEW,
  FIELD_ERROR_MESSAGES,
  METADATA_ADDED_TO_LIBRARY_KEY,
  MODE_RESTRICTED_ASSESSMENT_VIEW,
  MODE_EVALUATION_INSIGHT,
  QUESTION_UID_MULTI_PART,
  FIELD_UID_WORKBOOK_RESPONSE,
  QUESTION_UID_TEXT_RESPONSE_V3,
  FIELD_UID_DRAG_AND_DROP,
  FIELD_UID_TEXT_RESPONSE_SETTING,
  FIELD_UID_WORKBOOK_TEMPLATE,
  FIELD_UID_DRAG_AND_DROP_RESPONSE,
  FIELD_UID_DRAG_AND_DROP_ANSWERS,
  FIELD_UID_FIND_HOTSPOT_RESPONSE,
  FIELD_UID_FIND_HOTSPOT,
  FIELD_UID_FIND_HOTSPOT_ANSWERS,
  FIELD_UID_TABLE,
  FIELD_UID_TABLE_ANSWERS,
  FIELD_UID_ADD_LEARNING_GOALS,
  FIELD_UID_MATCHING_RESPONSE,
  FIELD_UID_MATCHING_ANSWERS,
  FIELD_UID_MATCHING,
  FIELD_UID_LABELLING,
  FIELD_UID_LABELLING_RESPONSE,
  FIELD_UID_LABELLING_ANSWERS,
  QUESTION_UID_SEPARATOR,
  FIELD_UID_AUDIO_RESPONSE,
  FIELD_UID_VIDEO_RESPONSE,
  RTE_HEIGHT_QUESTION_RESPONSE_FIELD,
  RTE_HEIGHT_QUESTION_RESPONSE_FIELD_TABLET,
  DOC_EDITOR_HEIGHT_TABLET,
  DOC_EDITOR_HEIGHT,
} from "../../modules/WorksheetConstants";
import {
  updateQuestionFieldsInCache,
  getFieldValue,
  getIsFieldHidden,
  decodeBlanksContent,
  getFieldType,
  getFieldComponentMode,
  getIsFieldEmpty,
  getUpdatedSubmissionFieldsFromCache,
  getIsSubQuestionSelected,
  validateCustomQuestionField,
  updateQuestionMetadataInCache,
  getQuestionFieldsFromTemplate,
  updateQuestionSubmissionInCache,
  getTableWithoutAnswers,
  getTableAnswers,
  isValidRevamped,
  getDataForAssessmentToolsDetails,
  isWorksheetWebView,
  updateQuestionMetadataInCacheV2,
  isStudentEndMode,
  decodeBlanksContentDocEditor,
} from "../../modules/WorksheetUtils";
import {
  isWorksheetQueriesV2Enabled,
  isWorksheetMutationsV2Enabled,
  getIsWorksheetDocEditorEnabled,
} from "../../modules/WorksheetFeatureFlags";
import {
  updateQuestion,
  updateActiveQuestion,
  updateQuestionSubmission,
  disableScrollEvent,
  updateErrorState,
} from "../../modules/WorksheetModules";
import classes from "./Question.scss";
import Header from "./Header";
import TextBox from "./TextBox";
import DynamicFields from "./DynamicFields";
import SubQuestions from "./SubQuestions";
import QuestionFieldComponent from "./QuestionFieldComponent";
import QuestionFieldLabel from "./QuestionFieldLabel";
import QuestionErrorMessage from "./QuestionErrorMessage";
import MediaUploadButton from "../MediaUploadButton";
import AddToLibraryModal from "../AddToLibraryModal";
import { Sentry } from "services/sentry";
import Separator from "./Separator";
import { updateQuestionSubmissionInCacheV2 } from "./QuestionUtils";

const Question = memo(props => {
  const {
    id,
    resourceId,
    questionIndex,
    headerConfig,
    questionTypeUid,
    questionTypeConfig,
    mode,
    submissionId,
    questionFields,
    submissionFields,
    updateQuestion,
    updateQuestionSubmission,
    onDeleteClick,
    isActiveQuestion,
    updateActiveQuestion,
    assessmentTools,
    assessmentToolsEvaluationDetails,
    subQuestions,
    isSubQuestion,
    isCreateMode,
    comment,
    setQuestionRef,
    isSubQuestionSelected,
    activeQuestion,
    submissionItem,
    questionData,
    disableScrollEvent,
    showErrorState,
    updateErrorState,
    isFromLibrary,
    isFromQuestionPreview,
    isAddedToSchoolLibrary,
    isFromInternalTool,
    isFromToddleLibraryPortal,
    portalType,
    subQuestionParentId,
    showWorksheetAssistant,
    evaluationInsights,
    questionSettings,
    parentEntityDetails,
    duplicateSectionChildResource,
    isEvaluated,
    learningGoalsDropdownOptions,
    learningGoalsFields,
    learningGoalsMapping,
    getLearningGoalsMappingWrapper,
    handleShowQuestionAssistant,
    questionTitleText,
    isStudentWorkbookForceViewMode,
    fieldList,
    userType,
    isPreviewMode,
    isLibraryStudentPreview,
    isAIAssessmentToolsOpen,
    getAddButton,
    addQuestionsInBetweenProps,
    isGeneratingAIQuestions,
    allFields,
    parentItemId,
    isQueriesV2Enabled,
    onMoveUp,
    onMoveDown,
    isFirstSubQuestion,
    isLastSubQuestion,
    unitPlanTemplate,
    isLockScreenEnabled,
  } = props;

  const { t } = useI18n(["common"]);
  const isInSafeExamBrowser = isSafeExamBrowser();
  const [isSubQuestionActive, setIsSubQuestionActive] = useState(false);

  const [localSubmissionFields, setLocalSubmissionFields] = useState(
    submissionFields
  );
  const [errors, setErrors] = useState([]);
  const [showAddToLibraryModal, setShowAddToLibraryModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const currentFields = _.includes(
    [
      MODE_TEACHER_CREATE,
      MODE_ASSESSMENT_VIEW,
      MODE_RESTRICTED_ASSESSMENT_VIEW,
      MODE_LIBRARY_VIEW,
    ],
    mode
  )
    ? questionFields
    : _.isEqual(mode, MODE_TEACHER_PREVIEW)
    ? localSubmissionFields
    : submissionFields;

  /**
   * template fields - fields that are automatically added from the question types' config
   * dynamic fields - additional fields that are added to the question after creation
   */

  const templateFields = getQuestionFieldsFromTemplate({
    mode,
    questionTypeConfig,
    questionSettings,
    questionTypeUid,
  });

  const dynamicFields = useMemo(() => {
    const DYNAMIC_FIELDS_UIDS = [...DYNAMIC_FIELDS];

    if (isCreateMode) {
      if (!(!isActiveQuestion && questionTypeUid !== QUESTION_UID_MULTI_PART)) {
        DYNAMIC_FIELDS_UIDS.push(..._.keys(learningGoalsFields));
      }
    } else {
      /** `add learning goals` only available in worksheet creation revamp */
      _.remove(DYNAMIC_FIELDS_UIDS, fieldUid =>
        _.isEqual(FIELD_UID_ADD_LEARNING_GOALS, fieldUid)
      );
    }

    return _.filter(questionFields, field =>
      _.includes(DYNAMIC_FIELDS_UIDS, _.get(field, "uid"))
    );
  }, [
    isCreateMode,
    learningGoalsFields,
    questionFields,
    isActiveQuestion,
    questionTypeUid,
  ]);

  const fieldDetailsList = _.includes(
    [
      MODE_TEACHER_CREATE,
      MODE_ASSESSMENT_VIEW,
      MODE_RESTRICTED_ASSESSMENT_VIEW,
      MODE_LIBRARY_VIEW,
    ],
    mode
  )
    ? _.get(questionTypeConfig, "field_list", {})
    : _.get(questionTypeConfig, "student_field_list", {});

  const disableQuestion =
    (!isActiveQuestion && showWorksheetAssistant) || // applying blur effect on inactive question
    isAIAssessmentToolsOpen; // applying blur effect when assessment tools assistant is open

  const containerClasses = classNames({
    [classes.container]: !isFromLibrary,
    [classes.containerQuestion]: isFromLibrary && !isSubQuestion,
    [classes.containerSubQuestion]: isFromLibrary && isSubQuestion,
    [classes.containerSubQuestionAssessmentView]:
      isFromLibrary &&
      isSubQuestion &&
      _.isEqual(MODE_ASSESSMENT_VIEW, MODE_RESTRICTED_ASSESSMENT_VIEW, mode),
    [classes.createContainer]:
      _.includes(
        [
          MODE_TEACHER_CREATE,
          MODE_ASSESSMENT_VIEW,
          MODE_RESTRICTED_ASSESSMENT_VIEW,
          MODE_LIBRARY_VIEW,
        ],
        mode
      ) && !isFromInternalTool,
    [classes.borderBottomZero]: isFromQuestionPreview,
    [classes.activeContainer]: isActiveQuestion && !isFromInternalTool,
    [classes.marginContainer]:
      !isSubQuestion &&
      !isFromLibrary &&
      !(isCreateMode || isGeneratingAIQuestions),
    [classes.marginContainerImportFromLibrary]: !isSubQuestion && isFromLibrary,
    [classes.errorContainer]: showErrorState,
    [classes.activeErrorContainer]: showErrorState && isActiveQuestion,
    [classes.containerHoverState]:
      !showErrorState && !isActiveQuestion && isCreateMode,
    [classes.evalutaionInsightsPadding]: _.includes(
      [MODE_EVALUATION_INSIGHT],
      mode
    ),
    [classes.containerStudentPreview]: isLibraryStudentPreview,

    // * Revamp classes
    [classes.createContainerRevamped]: isCreateMode && !isFromLibrary,
    [classes.createContainerInactiveRevamped]: disableQuestion,
    [classes.containerV2]: !isActiveQuestion && isCreateMode,
    // student revamp UI
    [classes.revampedStudentUI]: isStudentEndMode(mode) && !isFromInternalTool,
    [classes.ipadContainer]: isMobileOrTablet(),
  });

  const showDocEditor = getIsWorksheetDocEditorEnabled();
  const isStudentEndTablet = isStudentEndMode(mode) && isTabletByUserAgent();

  const editorHeightInTablet = showDocEditor
    ? DOC_EDITOR_HEIGHT_TABLET
    : RTE_HEIGHT_QUESTION_RESPONSE_FIELD_TABLET;

  const editorHeight = showDocEditor
    ? DOC_EDITOR_HEIGHT
    : RTE_HEIGHT_QUESTION_RESPONSE_FIELD;

  const commentConfig = _.get(COMMENT_FIELD_DETAILS, "fieldConfig.config", {});

  const containerStyle =
    isCreateMode && isActiveQuestion
      ? { boxShadow: "var(--elevation-2-bottom)" }
      : isActiveQuestion && !isFromInternalTool && isCreateMode
      ? elevations["elevation-2B"]
      : {};

  const questionContainerRef = useRef(null);

  const fieldRefs = useRef({});
  const questionRef = useRef({
    toggleErrorState: () => {},
    fieldRefs: fieldRefs.current,
    containerRef: questionContainerRef,
  });

  useEffect(() => {
    setQuestionRef(questionRef, id);
  }, [questionRef, id]);

  useEffect(() => {
    // Scroll to the question when its selected
    if (
      id == activeQuestion &&
      questionContainerRef?.current &&
      !isElementInViewPortVertical({
        element: questionContainerRef?.current,
      })
    ) {
      disableScrollEvent(true);
      setTimeout(() => {
        disableScrollEvent(false);
      }, 750);
      const behavior =
        isFromInternalTool && questionTypeUid == QUESTION_UID_MULTI_PART
          ? "auto"
          : "smooth";
      const block =
        _.isEqual(mode, MODE_EVALUATION_INSIGHT) ||
        _.isEqual(mode, MODE_TEACHER_CREATE) ||
        _.isEqual(mode, MODE_STUDENT_ATTEMPT)
          ? "start"
          : "nearest";
      scrollIntoView({
        element: questionContainerRef.current,
        block,
        behavior,
      });
    }

    if (questionTypeUid == QUESTION_UID_MULTI_PART) {
      setIsSubQuestionActive(
        _.some(subQuestions, question => {
          return _.get(question, "id") == activeQuestion;
        })
      );
    }
  }, [activeQuestion]);

  useEffect(() => {
    if (isCreateMode) {
      /** update reference of toggleErrorState when field value changes */
      questionRef.current.toggleErrorState = toggleErrorState;

      /** check if error has been fixed and remove the error state */
      /* Update the error state */
      if (showErrorState) {
        updateErrorState({
          questionId: id,
          value: !!isValidRevamped(currentFields, errors, setErrors),
        });
      }
    }
  }, [questionFields]);

  const toggleErrorState = () => {
    if (!isCreateMode) return;
    // checks if the question is valid and returns the updated the error state
    return {
      questionId: id,
      value: !!isValidRevamped(currentFields, errors, setErrors),
    };
  };

  const toggleAddToLibraryModal = useCallback(() => {
    setShowAddToLibraryModal(prev => !prev);
  }, []);

  const isValid = () => {
    let totalCount = 0;
    const errorsList = [];

    // check if score is not empty
    const scoreField = _.find(currentFields, { uid: FIELD_UID_SCORE });
    if (
      scoreField &&
      getIsFieldEmpty({
        value: _.get(scoreField, "value"),
        fieldUid: FIELD_UID_SCORE,
      })
    ) {
      totalCount++;
      errorsList.push(_.get(FIELD_ERROR_MESSAGES, FIELD_UID_SCORE));
    }

    // check if all the fields are valid
    _.map(templateFields, key => {
      if (_.get(fieldRefs, ["current", key], null)) {
        const isFieldValid = fieldRefs.current[key]?.isValid?.() || 0;

        if (isFieldValid > 0) {
          totalCount += isFieldValid;

          let errorMsg = "";
          if (key == FIELD_UID_BLANKS) {
            /** same filed has two different error messages */
            errorMsg = validateCustomQuestionField({
              fieldUid: key,
              value: _.get(_.find(currentFields, { uid: key }), "value", ""),
            });
          } else {
            errorMsg = _.get(FIELD_ERROR_MESSAGES, key, "");
          }
          errorsList.push(errorMsg);
        }
      }
    });

    if (!_.isEqual(errors, errorsList)) {
      setErrors(errorsList);
    }

    return totalCount;
  };

  const setFieldRef = (fieldUId, fieldRef) => {
    fieldRefs.current[fieldUId] = fieldRef;
  };

  const [debouncedUpdateQuestion] = useDebouncedCallback(
    updateQuestion,
    UPDATE_DEBOUNCE_CALL_TIME
  );

  const [debouncedUpdateQuestionSubmission] = useDebouncedCallback(
    updateQuestionSubmission,
    UPDATE_DEBOUNCE_CALL_TIME
  );

  /**
   * Determines the ID to position new items after, based on the feature flag status.
   * If worksheetMutationsV2 is enabled, uses the 'id' property from the last sub-question.
   * Otherwise, uses the 'resourceId' property from the last sub-question.
   *
   */
  const putAfterItemId = isWorksheetMutationsV2Enabled()
    ? _.get(_.last(subQuestions), "id")
    : _.get(_.last(subQuestions), "resourceId");

  const debouncedUpdateQuestionWrapper = useCallback(
    ({ id, fields, questionId, questionTypeUid, fieldUid }) => {
      Sentry.addBreadcrumb({
        category: "debouncedUpdateQuestionWrapper",
        level: "debug",
        message: JSON.stringify({
          id,
          fields,
          questionId,
          questionTypeUid,
          fieldUid,
          isCreateMode,
          isPreviewMode,
          submissionId,
        }),
      });

      if (isCreateMode) {
        debouncedUpdateQuestion({
          id,
          fields,
        });
      }
      if (_.isEqual(mode, MODE_STUDENT_ATTEMPT)) {
        debouncedUpdateQuestionSubmission({
          id: submissionId,
          fields,
          questionId,
          questionTypeUid,
          fieldUid,
        });
      }
    },
    [
      isCreateMode,
      submissionId,
      debouncedUpdateQuestionSubmission,
      debouncedUpdateQuestion,
      mode,
      isPreviewMode,
    ]
  );

  const updateQuestionWrapper = useCallback(
    ({ id, fields, questionId, questionTypeUid, fieldUid }) => {
      Sentry.addBreadcrumb({
        category: "updateQuestionWrapper",
        level: "debug",
        message: JSON.stringify({
          id,
          fields,
          questionId,
          questionTypeUid,
          fieldUid,
          isCreateMode,
          isPreviewMode,
          submissionId,
        }),
      });
      if (isCreateMode) {
        updateQuestion({
          id,
          fields,
        });
      }
      if (_.isEqual(mode, MODE_STUDENT_ATTEMPT)) {
        updateQuestionSubmission({
          id: submissionId,
          fields,
          questionId,
          questionTypeUid,
          fieldUid,
        });
      }
    },
    [
      isCreateMode,
      submissionId,
      updateQuestionSubmission,
      updateQuestion,
      mode,
      isPreviewMode,
    ]
  );

  const onFocusInputField = () => {
    if (!isActiveQuestion) {
      updateActiveQuestion(id);
    }
  };

  // Function to get array of questions from learningGoalsMapping
  const getGoalsTagCount = useCallback(
    ({ id }) => {
      let goalsTagCount = [];
      _.forEach(learningGoalsMapping, goal => {
        if (!_.isNil(goal[id])) {
          goalsTagCount = goal[id];
        }
      });
      return goalsTagCount;
    },
    [learningGoalsMapping]
  );

  // Function to set active question, when clicked on dropdown option
  const handleGoalsTagDropdownClick = useCallback(
    item => {
      updateActiveQuestion(_.get(item, "key"));
    },
    [updateActiveQuestion]
  );

  // Function to return the react element that would show learning goals count.
  const getLearningGoalsTagCountTooltip = useCallback(
    (nodeId, shouldUpdateActiveQuestionOnClick) => {
      if (mode !== MODE_TEACHER_CREATE || _.isNil(learningGoalsMapping))
        return null;

      const goalsTagCountArray = getGoalsTagCount({ id: nodeId });
      const goalsTagClasses = classNames(classes.goalsTag, {
        [classes.makeCursorDefault]: !shouldUpdateActiveQuestionOnClick,
      });
      return (
        <ConditionalWrapper
          type={"wrapperAsElement"}
          condition={_.size(goalsTagCountArray) > 0}
          wrapper={
            <Tooltip
              destroyTooltipOnScroll={false}
              tooltip={
                <DropdownMenu
                  options={goalsTagCountArray}
                  onClick={
                    shouldUpdateActiveQuestionOnClick
                      ? handleGoalsTagDropdownClick
                      : _.noop
                  }
                />
              }
              theme={"light"}
              showArrow={false}
              placement={"bottomRight"}
              overlayInnerStyle={{
                padding: 0,
                marginTop: "-8px",
                width: "250px",
              }}
            />
          }
        >
          <div className={goalsTagClasses}>
            <div>{goalsTagCountArray.length}</div>
            <TagOutlined variant="subtle" />
          </div>
        </ConditionalWrapper>
      );
    },
    [mode, learningGoalsMapping, getGoalsTagCount, handleGoalsTagDropdownClick]
  );
  const updateFields = useCallback(
    ({
      fields,
      isDebounce = false,
      isMutation = true,
      fieldUid,
      extraFields = {},
    }) => {
      updateQuestionFieldsInCache({
        fields,
        extraFields,
        isFromInternalTool,
        questionId: isFromInternalTool ? resourceId : id,
        isSubQuestion,
        subQuestionParentId,
        isQueriesV2Enabled,
      });

      if (
        _.includes(Object.keys(learningGoalsFields), _.get(extraFields, "uid"))
      ) {
        getLearningGoalsMappingWrapper(learningGoalsFields);
      }

      if (isAddedToSchoolLibrary) {
        /** reset added to library flag when any of the fields is updated  */
        if (isQueriesV2Enabled) {
          updateQuestionMetadataInCacheV2(id, {
            [METADATA_ADDED_TO_LIBRARY_KEY]: false,
          });
        } else {
          //WORKSHEET_RESTRUCTURING:Remove this once all questions are migrated to queries v2
          updateQuestionMetadataInCache(id, {
            [METADATA_ADDED_TO_LIBRARY_KEY]: false,
          });
        }
      }

      if (isMutation && mode !== MODE_TEACHER_PREVIEW) {
        if (isDebounce) {
          debouncedUpdateQuestionWrapper({
            id,
            fields,
            questionId: id,
            questionTypeUid,
            fieldUid,
          });
        } else {
          updateQuestionWrapper({
            id,
            fields,
            questionId: id,
            questionTypeUid,
            fieldUid,
          });
        }
      }

      if (mode === MODE_TEACHER_PREVIEW) {
        /** since we are not calling mutation in teacher preview mode, we have to manually update the fields */
        setLocalSubmissionFields(prev =>
          getUpdatedSubmissionFieldsFromCache(prev)
        );
      }
    },
    [
      isAddedToSchoolLibrary,
      mode,
      id,
      questionTypeUid,
      debouncedUpdateQuestionWrapper,
      updateQuestionWrapper,
      setLocalSubmissionFields,
    ]
  );

  const deleteQuestion = useCallback(() => {
    onDeleteClick(resourceId, id);
    updateActiveQuestion(null);
  }, [resourceId, onDeleteClick, updateActiveQuestion]);

  const duplicateQuestion = async () => {
    return await duplicateSectionChildResource({
      questionId: id,
      questionResourceId: resourceId,
      parentResourceId: subQuestionParentId ? subQuestionParentId : null,
      isSubQuestion: isSubQuestion,
      parentItemId,
    });
  };

  const onQuestionCardClick = e => {
    if (
      _.includes(
        [MODE_TEACHER_CREATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
        mode
      )
    ) {
      if (!isActiveQuestion) {
        updateActiveQuestion(id);
      }

      e.stopPropagation();
    }
  };

  const toggleMediaModal = useCallback(() => {
    setShowMediaModal(prev => !prev);
  }, []);

  const getQuestionComponent = () => {
    switch (questionTypeUid) {
      case CONTENT_UID_TEXT_BOX: {
        // we directly show the rich text editor instead of the card ui for text box
        const fieldData = _.find(questionFields, {
          uid: FIELD_UID_RICH_TEXT_EDITOR,
        });

        return _.isEmpty(fieldData) ? null : (
          <TextBox
            questionContainerRef={questionContainerRef}
            deleteQuestion={deleteQuestion}
            updateFields={updateFields}
            isCreateMode={isCreateMode}
            {...fieldData}
            mode={mode}
            fieldMode={isCreateMode && isActiveQuestion ? "edit" : "view"}
            isSubQuestion={isSubQuestion}
            updateActiveQuestion={updateActiveQuestion}
            customRef={setFieldRef}
            isActiveQuestion={isActiveQuestion}
            questionId={id}
            showErrorState={showErrorState}
            isFromLibrary={isFromLibrary}
            disableTextBox={disableQuestion}
            isGeneratingAIQuestions={isGeneratingAIQuestions}
            isFromInternalTool={isFromInternalTool}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            isFirstInstruction={isFirstSubQuestion}
            isLastInstruction={isLastSubQuestion}
            height={editorHeight}
          />
        );
      }

      case QUESTION_UID_SEPARATOR: {
        // we directly show the separator ui instead of the card ui
        return (
          <Separator
            isCreateMode={isCreateMode}
            deleteQuestion={deleteQuestion}
            disableSeparator={disableQuestion}
            isGeneratingAIQuestions={isGeneratingAIQuestions}
            isStudentEndMode={isStudentEndMode(mode)}
          />
        );
      }

      default: {
        return (
          <div
            className={containerClasses}
            style={containerStyle}
            ref={questionContainerRef}
            onClick={onQuestionCardClick}
            onKeyDown={e => {
              if (e.target === e.currentTarget) {
                onEnterOrSpaceKeyDown(e, onQuestionCardClick);
              }
            }}
            tabIndex={0}
            id={
              isActiveQuestion
                ? "active-question-card"
                : "active-question-toggle"
            }
            data-test-id={`worksheet-question-questionCard-${questionIndex}`}
          >
            {/* Header */}
            {!isFromQuestionPreview ? (
              <Header
                mode={mode}
                deleteQuestion={deleteQuestion}
                duplicateQuestion={duplicateQuestion}
                isActiveQuestion={isActiveQuestion}
                updateFields={updateFields}
                questionTypeUid={questionTypeUid}
                onAssessmentToolClick={toggleMediaModal}
                questionIndex={questionIndex}
                isFromLibrary={isFromLibrary}
                {...headerConfig}
                {..._.get(questionTypeConfig, "headerConfig", {})}
                toggleAddToLibraryModal={toggleAddToLibraryModal}
                isSubQuestion={isSubQuestion}
                isAddedToSchoolLibrary={isAddedToSchoolLibrary}
                isFromInternalTool={isFromInternalTool}
                isFromToddleLibraryPortal={isFromToddleLibraryPortal}
                portalType={portalType}
                showWorksheetAssistant={showWorksheetAssistant}
                isSubQuestionActive={isSubQuestionActive}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                isFirstSubQuestion={isFirstSubQuestion}
                isLastSubQuestion={isLastSubQuestion}
                handleShowQuestionAssistant={() => {
                  if (!_.isFunction(handleShowQuestionAssistant)) return;

                  // * For inactive question, first make them active and then open question assistant
                  if (questionRef.current && !isActiveQuestion) {
                    questionContainerRef.current.click();
                  }
                  setTimeout(() => handleShowQuestionAssistant(), 300);
                }}
                type={_.get(questionTypeConfig, "type")}
                assessmentToolsEvaluationDetails={
                  assessmentToolsEvaluationDetails
                }
                isLibraryStudentPreview={isLibraryStudentPreview}
                allFields={allFields}
                isCreateMode={isCreateMode}
              />
            ) : null}

            {/* Fields */}
            {_.map(templateFields, fieldUid => {
              const fieldDetails = _.get(fieldDetailsList, fieldUid, {});
              const fieldData = _.find(currentFields, { uid: fieldUid });

              const fieldId = _.get(fieldData, "id");
              const isSubmitted = _.get(
                assessmentToolsEvaluationDetails,
                "isSubmitted",
                false
              );

              const fieldMode = getFieldComponentMode({
                mode,
                fieldUid,
                isActiveQuestion,
                isSubmitted,
              });
              if (_.isEmpty(fieldDetails)) return null;

              const {
                type,
                viewType,
                label,
                config = {},
                fieldLabelConfig = {},
              } = fieldDetails;

              const questionFieldType = getFieldType({
                type,
                viewType,
                mode,
                isActiveQuestion,
              });

              const fieldValue = getFieldValue({
                fieldData,
                fieldKey: fieldUid,
                questionFields,
                fieldMode,
                questionFieldType,
              });

              const {
                placeholder = "",
                isRequired = false,
                fieldTypeConfig = {},
                parentType,
                options = [],
                showNoResponse = false,
                hasMediaSetting = false,
                styles = {},
                showOnlyOnActiveQuestion,
                hideViewModeLabel,
                customTextStyle,
                labelConfig,
                hideBorder = false,
              } = config;

              const showNoResponseText =
                showNoResponse &&
                !_.get(submissionItem, "isQuestionAttempted", false) &&
                !_.includes(
                  [
                    MODE_TEACHER_CREATE,
                    MODE_STUDENT_ATTEMPT,
                    MODE_RESTRICTED_ASSESSMENT_VIEW,
                    MODE_TEACHER_PREVIEW,
                  ],
                  mode
                );

              const isMediaAllowed = _.get(
                questionSettings,
                FIELD_UID_TEXT_RESPONSE_SETTING,
                false
              );
              const mediaModeCheck = _.includes(
                [MODE_STUDENT_ATTEMPT, MODE_TEACHER_PREVIEW],
                mode
              );
              const isMediaRestricted =
                mediaModeCheck && hasMediaSetting && !isMediaAllowed;

              const isLinkDisabled = hasMediaSetting
                ? null
                : mediaModeCheck && isInSafeExamBrowser;

              const hideField = getIsFieldHidden({
                showOnlyOnActiveQuestion,
                isActiveQuestion,
                mode,
                value: fieldValue,
                fieldUid,
              });

              const hideFieldLabel = hideViewModeLabel && !isActiveQuestion;

              const updateInputField = ({ params, isDebounce, isMutation }) => {
                switch (fieldUid) {
                  case FIELD_UID_BLANKS: {
                    const fieldValue = _.get(params, fieldUid, "") || "";

                    const answerField = _.find(questionFields, {
                      uid: FIELD_UID_BLANKS_ANSWERS,
                    });

                    const { questionText, answers } = showDocEditor
                      ? decodeBlanksContentDocEditor(fieldValue)
                      : decodeBlanksContent(fieldValue);

                    const fieldsToUpdate = [
                      {
                        id: fieldId,
                        value: questionText,
                      },
                      {
                        id: _.get(answerField, "id"),
                        value: {
                          answers,
                          fullField: fieldValue,
                        },
                      },
                    ];

                    updateFields({
                      fields: fieldsToUpdate,
                      isDebounce,
                      isMutation,
                      fieldUid,
                    });

                    break;
                  }
                  case FIELD_UID_MCQ_CHOICES: {
                    const answerField = _.find(questionFields, {
                      uid: FIELD_UID_MCQ_ANSWERS,
                    });

                    const questionFieldValue = _.get(params, fieldUid, []);
                    const questions = [];
                    const answers = [];

                    _.map(questionFieldValue, questionItem => {
                      if (_.isEmpty(_.get(questionItem, "id"))) return;
                      const { id, value, isSelected } = questionItem;

                      questions.push({ id, value });
                      answers.push({ id, isSelected });
                    });

                    const fieldsToUpdate = [
                      {
                        id: fieldId,
                        value: questions,
                      },
                    ];
                    if (_.get(answerField, "id")) {
                      fieldsToUpdate.push({
                        id: _.get(answerField, "id"),
                        value: answers,
                      });
                    }

                    updateFields({
                      fields: fieldsToUpdate,
                      isDebounce,
                      isMutation,
                      fieldUid,
                    });
                    break;
                  }
                  case FIELD_UID_MCQ_RESPONSE: {
                    const newFieldValue = _.get(params, fieldUid, null);

                    const updatedOptionsArray = _.map(newFieldValue, option => {
                      return {
                        id: option.id,
                        isStudentSelected: option.isStudentSelected,
                      };
                    });

                    updateFields({
                      fields: [
                        {
                          id: fieldId,
                          value: updatedOptionsArray,
                        },
                      ],
                      isDebounce,
                      isMutation,
                      fieldUid,
                    });

                    break;
                  }
                  case FIELD_UID_AUDIO_RESPONSE:
                  case FIELD_UID_WORKBOOK_RESPONSE:
                  case FIELD_UID_WORKBOOK_TEMPLATE:
                  case FIELD_UID_RESOURCES:
                  case FIELD_UID_VIDEO_RESPONSE:
                    break;
                  case FIELD_UID_DRAG_AND_DROP_RESPONSE:
                  case FIELD_UID_DRAG_AND_DROP: {
                    const dragAndDropAnswerField = _.find(questionFields, {
                      uid: FIELD_UID_DRAG_AND_DROP_ANSWERS,
                    });
                    const answerFieldId = _.get(dragAndDropAnswerField, "id");

                    const fields = _.get(params, "value")
                      ? [
                          {
                            id: fieldId,
                            value: _.get(params, "value"),
                          },
                        ]
                      : [];

                    if (_.isObject(_.get(params, "dragAndDropAnswers"))) {
                      fields.push({
                        id: answerFieldId,
                        value: _.get(params, "dragAndDropAnswers"),
                      });
                    }

                    updateFields({ fields, isDebounce, isMutation, fieldUid });
                    break;
                  }
                  case FIELD_UID_FIND_HOTSPOT_RESPONSE:
                  case FIELD_UID_FIND_HOTSPOT: {
                    const dragAndDropAnswerField = _.find(questionFields, {
                      uid: FIELD_UID_FIND_HOTSPOT_ANSWERS,
                    });
                    const answerFieldId = _.get(dragAndDropAnswerField, "id");

                    const fields = [
                      {
                        id: fieldId,
                        value: _.get(params, "value"),
                      },
                    ];

                    if (_.isArray(_.get(params, "findHotspotAnswers"))) {
                      fields.push({
                        id: answerFieldId,
                        value: _.get(params, "findHotspotAnswers"),
                      });
                    }

                    updateFields({ fields, isDebounce, isMutation, fieldUid });
                    break;
                  }
                  case FIELD_UID_TABLE: {
                    const answerFieldId = _.get(
                      _.find(questionFields, { uid: FIELD_UID_TABLE_ANSWERS }),
                      "id"
                    );

                    const fieldsToUpdate = [];
                    if (fieldMode === "create") {
                      fieldsToUpdate.push({
                        id: answerFieldId,
                        value: _.get(
                          params,
                          [FIELD_UID_TABLE_ANSWERS, "data"],
                          {}
                        ),
                      });
                      const tableWithoutAnswers = getTableWithoutAnswers(
                        _.get(params, [FIELD_UID_TABLE_ANSWERS], {})
                      );
                      fieldsToUpdate.push({
                        id: fieldId,
                        value: tableWithoutAnswers,
                      });
                    } else {
                      fieldsToUpdate.push({
                        id: fieldId,
                        value: {
                          ..._.get(params, [fieldUid]),
                          data: getTableAnswers(
                            _.get(params, [fieldUid, "data"])
                          ),
                        },
                      });
                    }
                    updateFields({
                      fields: fieldsToUpdate,
                      isDebounce,
                      isMutation,
                      fieldUid,
                    });
                    break;
                  }
                  case FIELD_UID_MATCHING:
                  case FIELD_UID_MATCHING_RESPONSE: {
                    const matchingAnswerField = _.find(questionFields, {
                      uid: FIELD_UID_MATCHING_ANSWERS,
                    });
                    const answerFieldId = _.get(matchingAnswerField, "id");

                    const fields = [
                      {
                        id: fieldId,
                        value: _.get(params, "value"),
                      },
                    ];

                    if (_.isObject(_.get(params, FIELD_UID_MATCHING_ANSWERS))) {
                      fields.push({
                        id: answerFieldId,
                        value: _.get(params, FIELD_UID_MATCHING_ANSWERS),
                      });
                    }

                    updateFields({ fields, isDebounce, isMutation, fieldUid });
                    break;
                  }

                  case FIELD_UID_LABELLING:
                  case FIELD_UID_LABELLING_RESPONSE: {
                    const labellingAnswerField = _.find(questionFields, {
                      uid: FIELD_UID_LABELLING_ANSWERS,
                    });
                    const answerFieldId = _.get(labellingAnswerField, "id");

                    const fields = [
                      {
                        id: fieldId,
                        value: _.get(params, "value"),
                      },
                    ];

                    if (_.isObject(_.get(params, "labellingAnswer"))) {
                      fields.push({
                        id: answerFieldId,
                        value: _.get(params, "labellingAnswer"),
                      });
                    }

                    updateFields({ fields, isDebounce, isMutation, fieldUid });
                    break;
                  }
                  default: {
                    const fieldsToUpdate = [
                      {
                        id: fieldId,
                        value: params[fieldUid],
                      },
                    ];

                    updateFields({
                      fields: fieldsToUpdate,
                      isDebounce,
                      isMutation,
                      fieldUid,
                    });
                    break;
                  }
                }
                if (_.isEqual(mode, MODE_STUDENT_ATTEMPT)) {
                  if (isQueriesV2Enabled) {
                    updateQuestionSubmissionInCacheV2({
                      question: questionData,
                      submissionId,
                      parentEntityDetails,
                      portalType,
                    });
                  } else {
                    //WORKSHEET_RESTRUCTURING:Remove else part
                    updateQuestionSubmissionInCache({
                      question: questionData,
                      submissionId,
                      parentEntityDetails,
                      portalType,
                      userType,
                      mode,
                    });
                  }
                }
              };

              const labelComponent =
                _.isEmpty(fieldLabelConfig) || hideFieldLabel ? null : (
                  <QuestionFieldLabel
                    {...fieldLabelConfig}
                    isRequired={isRequired}
                    isQuestionField={true}
                    portalType={portalType}
                  />
                );

              // Allow resources upload button for these question types in student attempt mode
              const textResponseMediaRestriction = _.isEqual(
                _.get(questionTypeConfig, "type"),
                QUESTION_UID_TEXT_RESPONSE_V3
              )
                ? !isMediaRestricted
                : false;

              const showUploadButton =
                textResponseMediaRestriction &&
                fieldUid == FIELD_UID_RESOURCES &&
                mode == MODE_STUDENT_ATTEMPT;

              const height =
                _.isEqual(fieldUid, "response") && isStudentEndTablet
                  ? editorHeightInTablet
                  : editorHeight;

              return (
                <>
                  {showUploadButton ? (
                    <MediaUploadButton
                      currentFields={currentFields}
                      t={t}
                      updateInputField={updateInputField}
                    />
                  ) : null}
                  <QuestionFieldComponent
                    hideField={hideField}
                    type={questionFieldType}
                    label={hideFieldLabel ? undefined : label}
                    labelComponent={labelComponent}
                    placeholder={placeholder}
                    fieldId={fieldId}
                    parentId={fieldId}
                    parentType={parentType}
                    fieldValue={fieldValue}
                    updateInputField={updateInputField}
                    options={options}
                    fieldTypeConfig={fieldTypeConfig}
                    isRequired={isRequired}
                    fieldUid={fieldUid}
                    mode={mode}
                    fieldMode={fieldMode}
                    showNoResponseText={showNoResponseText}
                    componentStyles={styles}
                    questionFields={questionFields}
                    customTextStyle={customTextStyle}
                    customRef={setFieldRef}
                    isMediaRestricted={isMediaRestricted}
                    onFocusInputField={onFocusInputField}
                    isFromLibrary={isFromLibrary}
                    portalType={portalType}
                    questionIndex={questionIndex}
                    isSubQuestion={isSubQuestion}
                    subQuestionParentId={subQuestionParentId}
                    resourceId={resourceId}
                    questionId={id}
                    submissionId={submissionId}
                    questionSettings={questionSettings}
                    submissionFields={submissionFields}
                    assessmentToolsEvaluationDetails={
                      assessmentToolsEvaluationDetails
                    }
                    isActiveQuestion={isActiveQuestion}
                    isEvaluated={isEvaluated}
                    learningGoalsMapping={learningGoalsMapping}
                    learningGoalsFields={learningGoalsFields}
                    getLearningGoalsTagCountTooltip={
                      getLearningGoalsTagCountTooltip
                    }
                    questionTitleText={questionTitleText}
                    isSubmitted={isSubmitted}
                    isStudentWorkbookForceViewMode={
                      isStudentWorkbookForceViewMode
                    }
                    isFromInternalTool={isFromInternalTool}
                    questionTypeUid={questionTypeUid}
                    labelConfig={labelConfig}
                    isQueriesV2Enabled={isQueriesV2Enabled}
                    height={height}
                    hideBorder={hideBorder}
                    isLockScreenEnabled={isLockScreenEnabled}
                    isLinkDisabled={isLinkDisabled}
                  />
                </>
              );
            })}

            {/* Evaluation Insights */}
            {_.isEqual(mode, MODE_EVALUATION_INSIGHT) ? (
              <QuestionInsightsField
                evaluationInsights={evaluationInsights}
              ></QuestionInsightsField>
            ) : null}

            {/* Sub-questions */}
            {!_.isEmpty(subQuestions) ? (
              <SubQuestions
                questions={subQuestions}
                mode={mode}
                parentResourceId={resourceId}
                setQuestionRef={setQuestionRef}
                isFromLibrary={isFromLibrary}
                isFromInternalTool={isFromInternalTool}
                portalType={portalType}
                duplicateSectionChildResource={duplicateSectionChildResource}
                isActiveQuestion={isActiveQuestion}
                getAddButton={getAddButton}
                addQuestionsInBetweenProps={addQuestionsInBetweenProps}
                parentItemId={id}
                isQueriesV2Enabled={isQueriesV2Enabled}
                isLockScreenEnabled={isLockScreenEnabled}
              />
            ) : null}

            {/* Comment */}
            {!_.isEmpty(comment) && mode == MODE_STUDENT_EVALUATE ? (
              <div className={classes.comment}>
                <QuestionFieldComponent
                  type={_.get(COMMENT_FIELD_DETAILS, "fieldConfig.type", null)}
                  labelComponent={
                    <QuestionFieldLabel
                      icon={_.get(COMMENT_FIELD_DETAILS, "icon", null)}
                      labelLocale={_.get(COMMENT_FIELD_DETAILS, "localeLabel")}
                      portalType={portalType}
                    />
                  }
                  fieldValue={comment}
                  portalType={portalType}
                  {...commentConfig}
                />
              </div>
            ) : null}

            {/* Dynamic fields */}
            {!_.isEqual(mode, MODE_RESTRICTED_ASSESSMENT_VIEW) &&
            _.includes(
              [
                MODE_TEACHER_CREATE,
                MODE_TEACHER_EVALUATE,
                MODE_TEACHER_VIEW,
                MODE_STUDENT_EVALUATE,
                MODE_ASSESSMENT_VIEW,
                MODE_LIBRARY_VIEW,
              ],
              mode
            ) ? (
              <DynamicFields
                dynamicFields={dynamicFields}
                updateFields={updateFields}
                isActiveQuestion={isActiveQuestion}
                questionId={id}
                putAfterItemId={putAfterItemId}
                resourceId={resourceId}
                assessmentTools={assessmentTools}
                mode={mode}
                isSubQuestionSelected={isSubQuestionSelected}
                isFromInternalTool={isFromInternalTool}
                portalType={portalType}
                learningGoalsDropdownOptions={learningGoalsDropdownOptions}
                learningGoalsFields={learningGoalsFields}
                learningGoalsMapping={learningGoalsMapping}
                getLearningGoalsTagCountTooltip={
                  getLearningGoalsTagCountTooltip
                }
                isSubQuestion={isSubQuestion}
                fieldList={fieldList}
                questionFields={questionFields}
                {..._.get(questionTypeConfig, "dynamicFieldsConfig", {})}
                isQueriesV2Enabled={isQueriesV2Enabled}
                unitPlanTemplate={unitPlanTemplate}
              />
            ) : null}

            {/* Assessment tools */}
            {showMediaModal ? (
              <div className={classes.modalContent}>
                <MediaModal
                  mode="view"
                  toggleMediaModal={toggleMediaModal}
                  activeIndex={0}
                  assessmentTools={_.map(
                    reorderTools({ assessmentTools, hasEdges: true }),
                    tool => _.get(tool, "node")
                  )}
                  assessmentToolsEvaluationDetails={
                    assessmentToolsEvaluationDetails
                  }
                  showRevampedEvaluationUI={true}
                  showMediaInModal={true}
                  showAllTools={true}
                />
              </div>
            ) : null}
          </div>
        );
      }
    }
  };

  return (
    <>
      {getQuestionComponent()}
      {showErrorState ? (
        <QuestionErrorMessage
          errors={errors}
          isSubQuestion={isSubQuestion}
          questionTypeUid={questionTypeUid}
        />
      ) : null}
      {showAddToLibraryModal ? (
        <AddToLibraryModal
          toggleAddToLibraryModal={toggleAddToLibraryModal}
          t={t}
          questionId={id}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
      ) : null}
    </>
  );
});

const EMPTY_ARRAY = [];
const mapStateToProps = (state, ownProps) => {
  const {
    id,
    submissionItem,
    mode,
    subQuestions,
    assignmentId,
    assessmentToolsEvaluationDetails,
    isFromInternalTool,
    studentAssignmentId,
  } = ownProps;
  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  const parentEntityDetails = _.get(state, "worksheet.parentEntity");
  const isCreateMode = _.isEqual(MODE_TEACHER_CREATE, mode);
  const isPreviewMode = _.isEqual(MODE_TEACHER_PREVIEW, mode);
  const isActiveQuestion =
    _.includes(
      [MODE_TEACHER_CREATE, MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW],
      mode
    ) &&
    (id === activeQuestion || isFromInternalTool);
  const isSubQuestionSelected =
    subQuestions &&
    getIsSubQuestionSelected({
      subQuestions,
      activeQuestion,
    });

  // * Showing error only when clicking assign
  const canShowErrors = _.get(state, "worksheet.sidebar.showErrors", false);

  const showErrorState = isCreateMode
    ? _.get(state, ["worksheet", "errorState", id], false) && canShowErrors
    : false;

  const isFromToddleLibraryPortal =
    state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY;

  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id");
  const userType = _.get(userInfo, "user_type");
  const childID = _.get(userInfo, "childID");
  const academicYearId = getSelectedAcademicYearId(state);

  // * for `MYP_OBJECTIVE_RUBRIC` and `MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC` assessment tools
  const questionAssessmentToolTypes = _.map(
    _.get(ownProps, "assessmentTools.edges", []),
    "node.assessmentToolType"
  );

  const isNewToolsExists = _.some(questionAssessmentToolTypes, tool =>
    _.includes(
      [MYP_OBJECTIVE_RUBRIC, MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC],
      tool
    )
  );

  // Using helper function to get student assignment data
  const studentAssignmentDataV2 = getStudentAssignmentFromCacheV2({
    id: studentAssignmentId,
    userFilter: "ALL",
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
  });

  const allFields = _.get(
    studentAssignmentDataV2,
    ["assignment", "content", "allFields"],
    []
  );

  let _assessmentToolsEvaluationDetails = assessmentToolsEvaluationDetails;
  if (isNewToolsExists) {
    _assessmentToolsEvaluationDetails = getDataForAssessmentToolsDetails({
      assignmentId,
      userType,
      curriculumProgramType,
      userId,
      childID,
      academicYearId,
      questionFields: _.get(ownProps, "questionFields", []),
    });
    // Specific to web view, we need to merge the fields
    _assessmentToolsEvaluationDetails = isWorksheetWebView()
      ? _.merge(
          assessmentToolsEvaluationDetails,
          _assessmentToolsEvaluationDetails,
          {
            assessmentFields: allFields,
          }
        )
      : _.merge(
          assessmentToolsEvaluationDetails,
          _assessmentToolsEvaluationDetails
        );
  }

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  //Fetching feature flag value here to avoid passing isQueriesV2Enabled prop through all parent components
  // This is used to determine if the new query structure should be applied
  // V2 queries should be used (feature flag enabled AND not in Toddle Library and not from Internal Tool)
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled(
    false,
    isFromInternalTool
  );

  return {
    isCreateMode,
    activeQuestion,
    isActiveQuestion,
    submissionFields: _.get(submissionItem, "fields", EMPTY_ARRAY),
    submissionId: _.get(submissionItem, "id"),
    isSubQuestionSelected,
    showErrorState,
    isFromToddleLibraryPortal,
    parentEntityDetails,
    assessmentToolsEvaluationDetails: _assessmentToolsEvaluationDetails,
    userType,
    isPreviewMode,
    isGeneratingAIQuestions,
    isQueriesV2Enabled,
  };
};

const mapActionCreators = {
  updateQuestion,
  updateQuestionSubmission,
  updateActiveQuestion,
  disableScrollEvent,
  updateErrorState,
};

Question.propTypes = {
  id: PropTypes.string,
  resourceId: PropTypes.string,
  questionIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  questionTypeUid: PropTypes.string,
  headerConfig: PropTypes.object,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  questionFields: PropTypes.array,
  submissionFields: PropTypes.array,
  submissionId: PropTypes.string,
  submissionItem: PropTypes.object,
  assessmentToolsEvaluationDetails: PropTypes.object,
  subQuestions: PropTypes.array,
  onDeleteClick: PropTypes.func,
  isSubQuestion: PropTypes.bool,
  setQuestionRef: PropTypes.func,
  isSubQuestionSelected: PropTypes.bool,
  questionData: PropTypes.object,
  isFromLibrary: PropTypes.bool,
  isFromQuestionPreview: PropTypes.bool,
  isAddedToSchoolLibrary: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  subQuestionParentId: PropTypes.string,
  questionSettings: PropTypes.object,
  parentEntityDetails: PropTypes.object,
  learningGoalsDropdownOptions: PropTypes.arrayOf(PropTypes.object),
  learningGoalsFields: PropTypes.objectOf(PropTypes.object),
  handleShowQuestionAssistant: PropTypes.func,
  questionTitleText: PropTypes.string,
  isStudentWorkbookForceViewMode: PropTypes.bool,
  fieldList: PropTypes.object,
  isLibraryStudentPreview: PropTypes.bool,
  getAddButton: PropTypes.func,
  addQuestionsInBetweenProps: PropTypes.object,
  parentItemId: PropTypes.string,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  isFirstSubQuestion: PropTypes.bool,
  isLastSubQuestion: PropTypes.bool,
};

Question.defaultProps = {
  submissionItem: {},
  subQuestions: [],
  onDeleteClick: _.noop,
  isSubQuestion: false,
  questionFields: [],
  submissionFields: [],
  setQuestionRef: _.noop,
  questionData: {},
  isFromQuestionPreview: false,
  isAddedToSchoolLibrary: false,
  isFromInternalTool: false,
  parentEntityDetails: {},
  learningGoalsDropdownOptions: [],
  learningGoalsFields: {},
  learningGoalsMapping: {},
  getLearningGoalsMappingWrapper: _.noop,
  getLearningGoalsTagCountTooltip: _.noop,
  fieldList: {},
  isLibraryStudentPreview: false,
  getAddButton: _.noop,
  addQuestionsInBetweenProps: {},
  parentItemId: null,
  onMoveUp: _.noop,
  onMoveDown: _.noop,
  isFirstSubQuestion: false,
  isLastSubQuestion: false,
};

Question.displayName = "Question";

export default connect(mapStateToProps, mapActionCreators)(Question);
